/**
 * Build-time environment values (inlined by dotenv-webpack at compile time).
 * Each entry MUST use a literal `process.env.VAR` — dynamic `process.env[name]` is not replaced.
 */
export const BUILD_ENV = {
    NODE_ENV: typeof process.env.NODE_ENV === 'string' ? process.env.NODE_ENV : 'development',
    /** Single backend host (CRM, IAM, audit log, websocket). Default: https://crm-backend.naqdpay.af */
    /**
     * Restore production-style logout on 401 while `pnpm dev` is running.
     * See `auth-failure-policy.ts`.
     */
    ENABLE_AUTH_FAILURE_LOGOUT: typeof process.env.ENABLE_AUTH_FAILURE_LOGOUT === 'string'
        ? process.env.ENABLE_AUTH_FAILURE_LOGOUT.trim()
        : '',
    /** Force 401 auto-logout suppression even in production builds (debug only). */
    DISABLE_AUTH_FAILURE_LOGOUT: typeof process.env.DISABLE_AUTH_FAILURE_LOGOUT === 'string'
        ? process.env.DISABLE_AUTH_FAILURE_LOGOUT.trim()
        : '',
    /** Single backend host (CRM, IAM, audit log, websocket). Default: backendcallcenter.azizibank.af */
    BACKEND_URL: typeof process.env.BACKEND_URL === 'string' ? process.env.BACKEND_URL.trim() : '',
    /**
     * `1` — browser calls same-origin `/api`, `/user`, `/websocket`; webpack proxies to `BACKEND_URL`.
     * Recommended for local dev to avoid CORS.
     */
    USE_DEV_PROXY: typeof process.env.USE_DEV_PROXY === 'string' ? process.env.USE_DEV_PROXY.trim() : '',
    SIP_WS_URL: typeof process.env.SIP_WS_URL === 'string' ? process.env.SIP_WS_URL.trim() : '',
    SIP_DOMAIN_HOST: typeof process.env.SIP_DOMAIN_HOST === 'string' ? process.env.SIP_DOMAIN_HOST.trim() : '',
    SIP_EXTENSION: typeof process.env.SIP_EXTENSION === 'string' ? process.env.SIP_EXTENSION.trim() : '',
    SIP_AUTH_ID: typeof process.env.SIP_AUTH_ID === 'string' ? process.env.SIP_AUTH_ID.trim() : '',
    SIP_PASSWORD: typeof process.env.SIP_PASSWORD === 'string' ? process.env.SIP_PASSWORD.trim() : '',
    SIP_SUPERVISE_SNOOP_CODE: typeof process.env.SIP_SUPERVISE_SNOOP_CODE === 'string'
        ? process.env.SIP_SUPERVISE_SNOOP_CODE.trim()
        : '',
    SIP_SUPERVISE_WHISPER_CODE: typeof process.env.SIP_SUPERVISE_WHISPER_CODE === 'string'
        ? process.env.SIP_SUPERVISE_WHISPER_CODE.trim()
        : '',
    SIP_SUPERVISE_BARGE_CODE: typeof process.env.SIP_SUPERVISE_BARGE_CODE === 'string'
        ? process.env.SIP_SUPERVISE_BARGE_CODE.trim()
        : '',
    SIP_SUPERVISE_CONFERENCE_CODE: typeof process.env.SIP_SUPERVISE_CONFERENCE_CODE === 'string'
        ? process.env.SIP_SUPERVISE_CONFERENCE_CODE.trim()
        : '',
    SIP_SUPERVISE_FORCE_HANGUP_CODE: typeof process.env.SIP_SUPERVISE_FORCE_HANGUP_CODE === 'string'
        ? process.env.SIP_SUPERVISE_FORCE_HANGUP_CODE.trim()
        : '',
    SIP_SUPERVISE_CONFERENCE_ROOM: typeof process.env.SIP_SUPERVISE_CONFERENCE_ROOM === 'string'
        ? process.env.SIP_SUPERVISE_CONFERENCE_ROOM.trim()
        : '',
    ASTERISK_AMI_HOST: typeof process.env.ASTERISK_AMI_HOST === 'string' ? process.env.ASTERISK_AMI_HOST.trim() : '',
    ASTERISK_AMI_PORT: typeof process.env.ASTERISK_AMI_PORT === 'string' ? process.env.ASTERISK_AMI_PORT.trim() : '',
    ASTERISK_AMI_SCHEME: typeof process.env.ASTERISK_AMI_SCHEME === 'string' ? process.env.ASTERISK_AMI_SCHEME.trim() : '',
    ASTERISK_AMI_PROXY_TARGET: typeof process.env.ASTERISK_AMI_PROXY_TARGET === 'string'
        ? process.env.ASTERISK_AMI_PROXY_TARGET.trim()
        : '',
    ASTERISK_AMI_HTTP_URL: typeof process.env.ASTERISK_AMI_HTTP_URL === 'string'
        ? process.env.ASTERISK_AMI_HTTP_URL.trim()
        : '',
    ASTERISK_AMI_USERNAME: typeof process.env.ASTERISK_AMI_USERNAME === 'string'
        ? process.env.ASTERISK_AMI_USERNAME.trim()
        : '',
    ASTERISK_AMI_SECRET: typeof process.env.ASTERISK_AMI_SECRET === 'string' ? process.env.ASTERISK_AMI_SECRET.trim() : '',
    ASTERISK_AMI_TIMEOUT_MS: typeof process.env.ASTERISK_AMI_TIMEOUT_MS === 'string'
        ? process.env.ASTERISK_AMI_TIMEOUT_MS.trim()
        : '',
    /** Set by `npm run preview` so production bundles keep same-origin proxy paths. */
    WEBPACK_SERVE: typeof process.env.WEBPACK_SERVE === 'string' ? process.env.WEBPACK_SERVE.trim() : '',
    /** CRM team chat (SockJS + STOMP). `0`/`false` = off; unset or `1` = on in all builds. */
    ENABLE_CRM_CHAT: typeof process.env.ENABLE_CRM_CHAT === 'string' ? process.env.ENABLE_CRM_CHAT.trim() : '',
    CHAT_SOCKJS_PATH: typeof process.env.CHAT_SOCKJS_PATH === 'string' ? process.env.CHAT_SOCKJS_PATH.trim() : '',
    /**
     * `1` / `true` — SockJS connects to CRM host directly (cross-origin).
     * Default: same-origin `/websocket/...` via webpack/nginx proxy (required for auth on `/info`).
     */
    CHAT_CROSS_ORIGIN: typeof process.env.CHAT_CROSS_ORIGIN === 'string' ? process.env.CHAT_CROSS_ORIGIN.trim() : '',
    /**
     * `1` / `true` — Airplane Tickets uses the in-memory mock adapter.
     * Default (unset) — real NaqdPay merchant cancel/reschedule client.
     */
    ENABLE_AIRPLANE_TICKETS_MOCK: typeof process.env.ENABLE_AIRPLANE_TICKETS_MOCK === 'string'
        ? process.env.ENABLE_AIRPLANE_TICKETS_MOCK.trim()
        : '',
    /**
     * Optional origin for merchant flight-booking APIs (`/api/merchant/flight-bookings/...`).
     * Empty = same CRM `/api` host as BACKEND_URL (preferred). Examples:
     * k8s `https://backend.naqdpay.com`, front `https://backend.naqdpay.com`, pilot `https://npapi.naqdpay.af`.
     */
    MERCHANT_API_ORIGIN: typeof process.env.MERCHANT_API_ORIGIN === 'string' ? process.env.MERCHANT_API_ORIGIN.trim() : '',
};
export const IS_PRODUCTION_BUILD = BUILD_ENV.NODE_ENV === 'production';
/** Production build served as static files (no webpack dev-server proxies). */
export const IS_STATIC_PRODUCTION_DEPLOY = IS_PRODUCTION_BUILD &&
    BUILD_ENV.WEBPACK_SERVE !== '1' &&
    BUILD_ENV.WEBPACK_SERVE.toLowerCase() !== 'true';
